import angular from 'angular';
import template from './asset-location.html';
import SetAssetLocationTemplate from './pin-location-modal/set-asset-location.modal.html';
import * as AssetDetailsActions from '../../../../state/asset-details/asset-details.actions';

import LocationMapUtil from './asset-location-map.util';
import Helper from './asset-location.helper';

function AssetLocationController(
  $stateParams,
  $filter,
  $timeout,
  $uibModal,
  gettextCatalog,
  mapService,
  usersService,
  EquipmentService,
  timeSeriesAPI,
  moment,
  $ngRedux,
) {
  'ngInject';

  //= =========local variables ======

  const self = this;

  self.mapUtil = new LocationMapUtil(mapService, $filter, gettextCatalog);
  self.helper = new Helper();
  self.store = $ngRedux;
  self.EquipmentService = EquipmentService;
  self.timeSeriesAPI = timeSeriesAPI;
  self.$filter = $filter;
  self.$uibModal = $uibModal;
  self.$stateParams = $stateParams;
  self.$timeout = $timeout;

  self.gpsStatus = '';
  self.dataStatus = 'loading-gps';
  self.gpsCoordinates = [];
  self.lastGpsSeries = null;
  self.dates = {
    startDate: null,
    minStartDate: null,
    maxStartDate: null,
    endDate: null,
    minEndDate: null,
    maxEndDate: null,
  };
  self.dateOption = 1;
  self.paging = {
    current: 1,
    itemsPerPage: 8,
  };
  self.gpsCoordinatesPerPage = [];

  self.map = null;
  self.markersLayer = null;
  self.drawLayer = null;
  self.userCanSetLocation = false;

  //= =======================
  self.$onInit = () => {
    self.unsubscribe = self.store.connect(() => ({}), { ...AssetDetailsActions })(self);

    if (!self.locationSeries) {
      self.gpsStatus = 'no-gps';
      self.dataStatus = 'gps-loaded';
    } else {
      // this will fire get gps data after set the date range
      self.setDatesByOption(self.dateOption);
    }
    self.userCanSetLocation = usersService.hasRequiredFeatures(['assets.set_location']);
    self.createMap();
    self.init = true;
  };

  self.$onChanges = (changes) => {
    // re-act to refresh btn and ignore first time loading
    if (self.init) {
      const {
        refreshing: { currentValue: isRefreshing } = {},
        asset: {
          currentValue: { locationSeries: currentLocation },
          previousValue: { locationSeries: previousLocation },
        } = {},
      } = changes;

      if (isRefreshing && !currentLocation) {
        // Ensures the loading indicator shows when there is no location series so that it
        // wont show the map and then hide once it has received the location series data after it was pinned
        self.dataStatus = 'loading-gps';
      }

      if (
        isRefreshing ||
        (!!currentLocation && !previousLocation) ||
        (!!currentLocation && !!previousLocation && currentLocation.id !== previousLocation.id)
      ) {
        self.getAssetGpsData();
      }
    }
  };

  self.$onDestroy = () => {
    self.unsubscribe();
  };

  //= === date picker / options functionality ========
  self.startObserver = () => {
    self.dateOption = null;
    self.updateMinMaxDates();
    self.getAssetGpsData();
  };

  self.endObserver = () => {
    self.dateOption = null;
    self.updateMinMaxDates();
    self.getAssetGpsData();
  };

  self.updateMinMaxDates = () => {
    self.dates.minStartDate = moment().subtract(30, 'days').toDate();
    self.dates.maxStartDate = self.dates.endDate;
    self.dates.minEndDate = self.dates.startDate;
    self.dates.maxEndDate = moment().toDate();
  };

  self.setDatesByOption = (dateOption) => {
    self.dates.startDate = moment().subtract(dateOption, 'days').toDate();
    self.dates.endDate = moment().toDate();
    self.updateMinMaxDates();
    self.getAssetGpsData();
  };
  //= ==================================

  //= ==============  Pagination  =====
  self.loadPage = (pageNumber) => {
    self.gpsCoordinatesPerPage = self.helper.getGpsCoordinatesByPage(
      pageNumber,
      self.paging.itemsPerPage,
      self.gpsCoordinates,
    );
  };
  //= ==========================================

  // create the map to house the breadcrumbs
  self.createMap = () => {
    if (self.map) return;
    // Generate a new map
    self.map = mapService.createMapDiv('breadcrumbs-map');
    self.map.setView([40.737, -73.923], 3);
    // add Full screen map control
    L.control.fullscreen().addTo(self.map);

    // `fullscreenchange` Event that's fired when entering or exiting fullscreen.
    self.map.on('fullscreenchange', function () {
      if (!self.map.isFullscreen() && self.pinDropped) {
        self.map.fitBounds(self.markersLayer.getBounds());
      }
    });

    //
    self.map.on('zoomend', function () {
      const status = self.gpsStatus;
      if (status === 'bluetooth-gps') {
        self.updateMapLastLocationMarker(self.lastGpsSeries.last, status);
      }
    });

    // Add markers layer to the map
    self.markersLayer = L.geoJson();
    self.map.addLayer(self.markersLayer);

    if (self.userCanSetLocation) {
      // Initialize the FeatureGroup to store editable layers
      self.drawLayer = new L.FeatureGroup();
      self.drawLayer.addTo(self.map);
      const drawControl = self.mapUtil.createDrawPinControl(self.drawLayer);
      drawControl.addTo(self.map);

      self.addMapDrawPinEventHandlers();
    }

    angular
      .element(document.querySelector('#breadcrumbs-map'))
      .on('click', 'button.location-unpin', () => {
        self.unpinLocation();
      });
  };

  self.addMapDrawPinEventHandlers = () => {
    self.map.on('draw:canceled', function () {
      self.drawGpsMapMarkers();
    });

    // A map event that the user has started drawing on the map
    self.map.on('draw:drawstart', function () {
      // User is still in drawing mode
      self.pinDropped = false;
      // Clear layers from the map
      self.markersLayer.clearLayers();
      self.drawLayer.clearLayers();
      // drawing started, so disable panning and zooming
      // Otherwise, the map moves around while trying to draw
      self.map.touchZoom.disable();
      self.map.doubleClickZoom.disable();
      self.map.scrollWheelZoom.disable();
      self.map.dragging.disable();
    });

    //  A map event that the user has stopped drawing on the map
    self.map.on('draw:drawstop', function (event) {
      // self.pinDropped is used to check whether the user clicked the cancel
      // label on the marker drawing tool.
      // If the user places the pin draw:created is called
      // If the user cancels drawing before dropping the pin then we
      // simply re-draw the pins
      const type = event.layerType;
      if (type === 'marker') {
        if (self.pinDropped === false) {
          self.drawGpsMapMarkers();
        }
      }
      // drawing stopped, so re-enable panning and zooming
      self.map.touchZoom.enable();
      self.map.doubleClickZoom.enable();
      self.map.scrollWheelZoom.enable();
      self.map.dragging.enable();
      // Bring back the markers if cancelled.
      self.map.addLayer(self.markersLayer);
    });

    // listening for drawing events
    self.map.on('draw:created', function (event) {
      // User has dropped the pin and stopped drawing
      self.pinDropped = true;
      if (event.layerType === 'marker') {
        // get dropped location
        const latlng = event.layer.getLatLng();
        self.openAssetLocationConfirmModal(latlng);
        self.drawLayer.clearLayers();
      }
    });
  };

  self.getAssetGpsData = () => {
    if (!self.locationSeries) return;
    const gpsSeriesId = self.locationSeries.id;
    // this ensure there is a start and end date when the user clears the date pickers
    if (!self.dates.startDate) {
      self.dates.startDate = new Date();
      self.dates.startDate.setDate(self.dates.startDate.getDate() - 30); // 30 days ago
    }
    if (!self.dates.endDate) self.dates.endDate = new Date(); // current date

    const { startDate } = self.dates;
    const { endDate } = self.dates;

    // Adjust the start and end times in all scenarios except for when the user selected 24hrs
    // so that all other scenarios will start at the time 0:00:00:00 and end at 23:59:59:999 instead of both using the time it was ran on the user computer
    // example if the user selects jan 1 2001 for start and end but they run the search at 11:30 it will get the whole day instead of only in the millisecond it was run at
    if (self.dateOption !== 1) {
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }
    self.getGpsSeriesData(gpsSeriesId, startDate, endDate);
  };

  //= =========== gps data / history functionality ========================
  self.getGpsSeriesData = (gpsSeriesId, startDate, endDate) => {
    self.dataStatus = 'loading-gps';
    self.EquipmentService.getEquipSeriesData(
      [gpsSeriesId],
      [moment(startDate.toISOString()).toDate(), moment(endDate.toISOString()).toDate()],
    )
      .then((response = {}) => {
        const [gpSeriesResult] = response.data || [];
        const { series, data } = gpSeriesResult || {};

        self.gpsStatus = self.helper.getAssetGpsStatus(
          series,
          self.asset.networkTechnology === 'bluetooth',
        );
        self.lastGpsSeries = series;
        // fire an action to update gps in asset info bar
        if (series && !self.helper.isSameSeries(series, self.locationSeries)) {
          self.updateAssetLocation(series.last);
        }
        self.gpsCoordinates = self.setupLocationHistory(data);
        self.paging.current = 1;
        self.loadPage(1);
        self.drawGpsMapMarkers();
        self.dataStatus = 'gps-loaded';
      })
      .catch((e) => {
        self.dataStatus = 'error-gps';
        handleError(e);
        throw e;
      });
  };

  self.drawGpsMapMarkers = () => {
    const status = self.gpsStatus;

    self.markersLayer.clearLayers();
    if (status !== 'no-gps') {
      if (status === 'gps-in-30d') {
        self.gpsCoordinates.length
          ? self.updateMapLocationHistoryMarkers(self.gpsCoordinates)
          : self.updateMapLastLocationMarker(self.lastGpsSeries.last);
      } else if (['pinned-gps', 'bluetooth-gps', 'no-gps-in-30d'].includes(status)) {
        self.updateMapLastLocationMarker(self.lastGpsSeries.last, status);
      }
      // fit markers to the map container on loading of self.markersLayer layer.
      self.map.fitBounds(self.markersLayer.getBounds());
    }
  };
  //= ===============================================

  self.openMarkerPopup = (selectedMarker) => {
    self.markersLayer.eachLayer(function (marker) {
      if (marker.options.title === selectedMarker.title) {
        marker.openPopup();
      }
    });
  };

  /// ====================== ++++++

  self.updateMapLastLocationMarker = (lastLocation, gpsStatus) => {
    self.markersLayer.clearLayers();
    /* Custom marker to display last known location. */
    const zoomLevel = self.map.getZoom();
    const marker = self.mapUtil.createPinnedMarker(
      lastLocation,
      gpsStatus,
      zoomLevel,
      self.userCanSetLocation,
    );
    marker.on('mouseover', function () {
      this.openPopup();
    });
    marker.addTo(self.markersLayer);

    /* Add self.markersLayer to the map and fit the zoom to the screen. */
    self.map.addLayer(self.markersLayer);
  };

  self.updateMapLocationHistoryMarkers = (locationHistory = []) => {
    self.markersLayer.clearLayers();
    locationHistory.forEach((location, index) => {
      // Implement a custom marker to show every point in the gpsCoordinates object
      const marker = self.mapUtil.createBreadcrumbMarker(location, index);

      // Create a popup that contains the data related to eack particular marker.
      const [lng, lat] = location.v || [];
      const popupContent = self.mapUtil.generatebreadcrumbMarkerPopup(
        lat,
        lng,
        location.t,
        location.attrs,
      );

      // Attach our popup to every marker that hits the map
      marker.bindPopup(popupContent, {
        closeButton: true,
        minWidth: 200,
      });
      // allow a popup to show on hover over marker
      marker.on('mouseover', function () {
        this.openPopup();
      });
      // Add markers to self.markersLayer layer
      marker.addTo(self.markersLayer);
    }); // end loop to populate map with custom markers

    // Create polyLines
    const polylines = self.mapUtil.createPolylinesLayer(locationHistory);
    // add polylines to the map
    polylines.addTo(self.markersLayer);
  };

  self.setupLocationHistory = (locationHistory = []) => {
    // sort with most recent
    // making sure to have attrs object with heading, speed, title
    const updatedLocation = locationHistory.reverse().map((location = {}, index) => {
      const { attrs = {} } = location;
      location.attrs = { ...attrs, heading: attrs.heading || 0, speed: attrs.speed || 0 };
      location.title = index;
      return location;
    });
    return updatedLocation;
  };

  //= ====== export csv data   ===================== +++
  self.getCsvData = function () {
    const headers = {
      Timestamp: 'Timestamp',
      Latitude: 'Latitude',
      Longitude: 'Longitude',
    };

    const csvData = self.gpsCoordinates.map((coord) => {
      const timestamp = self.$filter('TableDateFilter')(coord.t);
      const [lng, lat] = coord.v || [];
      return {
        Timestamp: timestamp,
        Latitude: lat,
        Longitude: lng,
      };
    });

    return [headers, ...csvData];
  };

  self.getCsvFileName = function () {
    const date = moment().format('YY-MM-DDTHH:mm:ss');
    return `${self.asset.name}_LocationHistory_${date}.csv`;
  };
  //= ===================================

  function handleError(error = {}) {
    const { data = {}, status = '' } = error;
    const message = `${data.message} - ${status}`;
    const errorMsg = `${gettextCatalog.getString(
      'An error occurred while retrieving location information',
    )} ${message} ${gettextCatalog.getString('. Please try again later.')}`;
    self.errorText = errorMsg;
  }

  //= ======== pinning functions============================
  self.setAssetLocation = ({ lat, lng }) => {
    self.dataStatus = 'loading-gps';
    const coordinates = {
      latitude: lat,
      longitude: lng,
    };

    self.timeSeriesAPI
      .setLocation(
        self.locationSeries?.id,
        self.gpsStatus === 'pinned-gps',
        coordinates,
        self.asset.id,
      )
      .then(() => {}, handleError)
      .finally(() => {
        if (!self.locationSeries) {
          self.refreshAssetdashBoardData(self.asset.id);
        } else {
          self.getAssetGpsData();
        }
      });
  };

  self.unpinLocation = () => {
    self.dataStatus = 'loading-gps';
    self.timeSeriesAPI
      .setLocation(self.locationSeries.id)
      .then(() => {}, handleError)
      .finally(() => {
        self.getAssetGpsData();
      });
  };

  // Create a modal - User must confirm they want to set the Asset location
  self.openAssetLocationConfirmModal = function (latLng) {
    const appendToElement = self.map.isFullscreen()
      ? angular.element('#breadcrumbs-map')
      : undefined;
    self.$uibModal
      .open({
        animation: true,
        bindToController: true,
        template: SetAssetLocationTemplate,
        controller: 'SetAssetLocationController',
        resolve: {
          isAssetLocationConfirmed: () => false,
          pinnedAssetEvent: () => latLng,
          pinnedAssetModel: () => {},
        },
        size: 'md',
        appendTo: appendToElement,
      })

      // modal dialog result - called when user clicks Confirm Button
      .result.then((pinnedAssetModel) => {
        if (pinnedAssetModel.isConfirmed === true) {
          self.setAssetLocation(latLng);
        } else {
          self.drawGpsMapMarkers();
        }
      })
      .catch(() => {
        self.drawGpsMapMarkers();
      });
  };
}

export default {
  template,
  controller: AssetLocationController,
  controllerAs: 'lcVm',
  bindings: {
    locationSeries: '<',
    asset: '<',
    refreshing: '<',
  },
};
